/**
 * Overview Tab
 *
 * High-density dashboard showing all key metrics at a glance.
 * Features proper visual grouping for better organization.
 * Includes tooltips and recommendations for first-time understanding.
 */

import { useMemo } from "react";
import { formatCurrency, cn } from "@/lib/utils";
import { useTradingAnalysis } from "../trading-analysis-context";
import { StatCard } from "./shared/stat-card";
import { StatGrid } from "./shared/stat-grid";
import { AnalysisCard } from "./shared/analysis-card";
import { ProgressBar } from "./shared/mini-heatmap";
import { TraderScoreCard } from "./shared/trader-score-card";
import { PerformanceHeroCard } from "./shared/performance-hero-card";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import {
  FlameIcon,
  ScaleIcon,
} from "lucide-react";

/** Label with dashed underline and tooltip on hover */
function TooltipLabel({ 
  children, 
  tooltip,
  className 
}: { 
  children: React.ReactNode; 
  tooltip: string;
  className?: string;
}) {
  return (
    <TooltipPrimitive.Provider delayDuration={250}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>
          <span className={cn(
            "border-b border-dashed border-muted-foreground/50 cursor-default select-none",
            className
          )}>
            {children}
          </span>
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side="top"
            sideOffset={5}
            className={cn(
              "z-50 max-w-[280px] rounded-md px-3 py-2",
              "bg-card/95 text-foreground/90 backdrop-blur-sm",
              "border border-border dark:border-muted-foreground/20 shadow-lg",
              "animate-in fade-in-0 zoom-in-95",
              "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
              "data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2"
            )}
          >
            <p className="text-xs leading-relaxed">{tooltip}</p>
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

export function OverviewTab() {
  const {
    stats,
    filteredTrades,
    dateRange,
    behavior,
  } = useTradingAnalysis();

  // Calculate best/worst day from trades
  const { bestDay, worstDay } = useMemo(() => {
    if (!filteredTrades.length) {
      return {
        bestDay: { pnl: 0, date: "" },
        worstDay: { pnl: 0, date: "" },
      };
    }

    // Group trades by day and sum P&L
    const dailyPnl = new Map<string, number>();
    for (const trade of filteredTrades) {
      const day = trade.exitTime.toISOString().split("T")[0];
      const existing = dailyPnl.get(day) || 0;
      dailyPnl.set(day, existing + trade.netPnl);
    }

    let maxPnl = -Infinity, maxDay = "";
    let minPnl = Infinity, minDay = "";
    for (const [day, pnl] of dailyPnl) {
      if (pnl > maxPnl) { maxPnl = pnl; maxDay = day; }
      if (pnl < minPnl) { minPnl = pnl; minDay = day; }
    }

    return {
      bestDay: { pnl: maxPnl === -Infinity ? 0 : maxPnl, date: maxDay },
      worstDay: { pnl: minPnl === Infinity ? 0 : minPnl, date: minDay },
    };
  }, [filteredTrades]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const isEmpty = !filteredTrades.length;

  return (
    <div className="space-y-4">
      {/* Top Section: Performance Hero + Trader Score */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Performance Summary Card - takes more space */}
        <div className="lg:col-span-3">
          <PerformanceHeroCard
            stats={stats}
            trades={filteredTrades}
            dateRange={dateRange}
            isEmpty={isEmpty}
          />
        </div>

        {/* Trader Score Card */}
        <div className="lg:col-span-2">
          <TraderScoreCard stats={stats} behavior={behavior} />
        </div>
      </div>

      {/* Key Metrics Section */}
      <AnalysisCard
        title="Key Metrics"
        subtitle={getKeyMetricsInsight(stats)}
      >
        <StatGrid columns={4} gap="sm">
          <StatCard
            label="Total Trades"
            value={stats.totalTrades}
            subValue={`${stats.tradesPerDay.toFixed(1)}/day`}
            tooltip="Number of completed trades in the selected period"
          />
          <StatCard
            label="Win Rate"
            value={`${stats.winRate.toFixed(1)}%`}
            subValue={`${stats.winningTrades}W / ${stats.losingTrades}L`}
            variant="default"
            tooltip="Percentage of trades closed in profit. Above 50% is good, but must consider risk/reward ratio."
          />
          <StatCard
            label="Profit Factor"
            value={
              stats.profitFactor === Infinity
                ? "∞"
                : stats.profitFactor.toFixed(2)
            }
            variant="default"
            tooltip="Gross profit ÷ gross loss. Above 1.5 is good, above 2.0 is excellent. Below 1.0 means losing money."
          />
          <StatCard
            label="Expectancy"
            value={`${stats.expectancy >= 0 ? "+" : "-"}$${formatCurrency(Math.abs(stats.expectancy), 2)}`}
            variant={stats.expectancy >= 0 ? "positive" : "negative"}
            subValue="per trade"
            tooltip="Average expected profit per trade. A positive value means your strategy is profitable over time."
          />
        </StatGrid>
      </AnalysisCard>

      {/* Two-Column Section: Trade Results + Risk Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Trade Results Card */}
        <AnalysisCard
          title="Trade Results"
          subtitle="Win/loss breakdown"
          action={
            <div className="flex items-center gap-1.5 text-xs">
              <ScaleIcon className="size-3.5 text-muted-foreground" />
              <TooltipLabel 
                tooltip="Risk:Reward Ratio — Average win ÷ average loss. Above 1:1 means your wins are larger than losses. Above 2:1 is excellent."
                className="text-muted-foreground"
              >
                R:R {stats.riskRewardRatio.toFixed(2)}:1
              </TooltipLabel>
            </div>
          }
        >
          <div className="space-y-3">
            {/* Average Win/Loss */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="text-xs text-muted-foreground mb-1">
                  Average Win
                </div>
                <div className="text-lg font-bold text-bid-foreground">
                  ${formatCurrency(stats.averageProfit, 2)}
                </div>
              </div>
              <div className="bg-muted/30 rounded-lg p-3">
                <div className="text-xs text-muted-foreground mb-1">
                  Average Loss
                </div>
                <div className="text-lg font-bold text-ask-foreground">
                  ${formatCurrency(stats.averageLoss, 2)}
                </div>
              </div>
            </div>

            {/* Largest Win/Loss */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted/30 rounded-lg p-2.5">
                <div className="text-xs text-muted-foreground mb-0.5">
                  Largest Win
                </div>
                <div className="text-lg font-bold text-bid-foreground">
                  ${formatCurrency(stats.largestWin, 2)}
                </div>
              </div>
              <div className="bg-muted/30 rounded-lg p-2.5">
                <div className="text-xs text-muted-foreground mb-0.5">
                  Largest Loss
                </div>
                <div className="text-lg font-bold text-ask-foreground">
                  ${formatCurrency(stats.largestLoss, 2)}
                </div>
              </div>
            </div>
          </div>
        </AnalysisCard>

        {/* Risk Metrics Card */}
        <AnalysisCard
          title="Risk Metrics"
          subtitle={getRiskMetricsInsight(stats)}
        >
          <div className="space-y-2">
            {/* Max Drawdown */}
            <div className="bg-muted/30 rounded-lg p-2.5">
              <div className="flex items-center justify-between mb-0.5">
                <TooltipLabel 
                  tooltip="Largest peak-to-trough decline. Keep below 20% for sustainable trading."
                  className="text-xs text-muted-foreground"
                >
                  Max Drawdown
                </TooltipLabel>
                <span className="text-xs text-muted-foreground">
                  ${formatCurrency(stats.maxDrawdown, 0)}
                </span>
              </div>
              <div className="text-lg font-bold text-foreground">
                {stats.maxDrawdownPercent.toFixed(1)}%
              </div>
              <ProgressBar
                value={Math.min(stats.maxDrawdownPercent, 100)}
                variant="default"
              />
            </div>

            {/* Recovery + Ratios */}
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-muted/30 rounded-lg p-2.5">
                <div className="mb-0.5">
                  <TooltipLabel 
                    tooltip="Net profit ÷ max drawdown. Above 1.0 means you've recovered from your worst drawdown."
                    className="text-xs text-muted-foreground"
                  >
                    Recovery
                  </TooltipLabel>
                </div>
                <div className="text-sm font-bold text-foreground">
                  {stats.recoveryFactor.toFixed(2)}
                </div>
              </div>
              <div className="bg-muted/30 rounded-lg p-2.5">
                <div className="mb-0.5">
                  <TooltipLabel 
                    tooltip="Risk-adjusted return measure. Above 1.0 is good, above 2.0 is excellent."
                    className="text-xs text-muted-foreground"
                  >
                    Sharpe
                  </TooltipLabel>
                </div>
                <div className="text-sm font-bold text-foreground">
                  {stats.sharpeRatio !== null
                    ? stats.sharpeRatio.toFixed(2)
                    : "N/A"}
                </div>
              </div>
              <div className="bg-muted/30 rounded-lg p-2.5">
                <div className="mb-0.5">
                  <TooltipLabel 
                    tooltip="Like Sharpe but only penalizes downside volatility. Better for traders."
                    className="text-xs text-muted-foreground"
                  >
                    Sortino
                  </TooltipLabel>
                </div>
                <div className="text-sm font-bold text-foreground">
                  {stats.sortinoRatio !== null
                    ? stats.sortinoRatio.toFixed(2)
                    : "N/A"}
                </div>
              </div>
            </div>
          </div>
        </AnalysisCard>
      </div>

      {/* Bottom Section: Streaks & Profile + Time Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Streaks & Profile */}
        <AnalysisCard title="Streaks & Profile">
          <div className="space-y-3">
            {/* Current Streak */}
            <div
              className="flex items-center justify-between p-3 rounded-lg bg-muted/30"
            >
              <div className="flex items-center gap-2">
                <FlameIcon
                  className={cn(
                    "size-5",
                    stats.currentStreakType === "win" && "text-bid-foreground",
                    stats.currentStreakType === "loss" && "text-ask-foreground",
                    stats.currentStreakType === "none" && "text-muted-foreground"
                  )}
                />
                <span className="text-sm font-medium">Current Streak</span>
              </div>
              <span
                className={cn(
                  "text-xl font-bold",
                  stats.currentStreakType === "win" && "text-bid-foreground",
                  stats.currentStreakType === "loss" && "text-ask-foreground"
                )}
              >
                {stats.currentStreak}{" "}
                {stats.currentStreakType === "win"
                  ? "wins"
                  : stats.currentStreakType === "loss"
                    ? "losses"
                    : "—"}
              </span>
            </div>

            {/* Best Day / Worst Day */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted/30 rounded-lg p-2.5">
                <div className="text-xs text-muted-foreground mb-0.5">
                  Best Day
                </div>
                <div className="text-lg font-bold text-bid-foreground">
                  ${formatCurrency(Math.abs(bestDay.pnl), 0)}
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {formatDate(bestDay.date)}
                </div>
              </div>
              <div className="bg-muted/30 rounded-lg p-2.5">
                <div className="text-xs text-muted-foreground mb-0.5">
                  Worst Day
                </div>
                <div className="text-lg font-bold text-ask-foreground">
                  -${formatCurrency(Math.abs(worstDay.pnl), 0)}
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5">
                  {formatDate(worstDay.date)}
                </div>
              </div>
            </div>

            {/* Best Win Streak / Worst Loss Streak */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-muted/30 rounded-lg p-2.5">
                <div className="text-xs text-muted-foreground mb-0.5">
                  Best Win Streak
                </div>
                <div className="text-lg font-bold text-bid-foreground">
                  {stats.longestWinStreak}
                  <span className="text-xs font-normal ml-1">wins</span>
                </div>
              </div>
              <div className="bg-muted/30 rounded-lg p-2.5">
                <div className="text-xs text-muted-foreground mb-0.5">
                  Worst Loss Streak
                </div>
                <div className="text-lg font-bold text-ask-foreground">
                  {stats.longestLossStreak}
                  <span className="text-xs font-normal ml-1">losses</span>
                </div>
              </div>
            </div>
          </div>
        </AnalysisCard>

        {/* Trading Activity */}
        <AnalysisCard
          title="Trading Activity"
          subtitle="Time and frequency metrics"
        >
          <div className="space-y-3">
            <StatGrid columns={3} gap="sm">
              <StatCard
                label="Trading Days"
                value={stats.tradingDays}
                tooltip="Number of unique days with at least one trade"
              />
              <StatCard
                label="Avg Duration"
                value={formatDuration(stats.avgTradeDurationMs)}
                tooltip="Average time between opening and closing a position"
              />
              <StatCard
                label="Avg Trade"
                value={`${stats.averageTrade >= 0 ? "+" : ""}$${formatCurrency(Math.abs(stats.averageTrade), 2)}`}
                variant={stats.averageTrade >= 0 ? "positive" : "negative"}
                tooltip="Average profit or loss per trade after fees"
              />
            </StatGrid>

            {/* Volume & Fees */}
            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border/30">
              <div className="bg-muted/30 rounded-lg p-2.5">
                <div className="text-[10px] text-muted-foreground mb-0.5">
                  Total Volume
                </div>
                <div className="text-sm font-bold text-foreground">
                  ${formatCurrency(stats.totalVolume, 0)}
                </div>
              </div>
              <div className="bg-muted/30 rounded-lg p-2.5">
                <div className="text-[10px] text-muted-foreground mb-0.5">
                  Total Fees
                </div>
                <div className="text-sm font-bold text-foreground">
                  ${formatCurrency(stats.totalFees, 2)}
                </div>
              </div>
            </div>

            {/* Gross vs Net */}
            <div className="bg-muted/20 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] text-muted-foreground">
                    Gross P&L
                  </div>
                  <div
                    className={cn(
                      "text-sm font-bold",
                      stats.totalPnl >= 0
                        ? "text-bid-foreground"
                        : "text-ask-foreground"
                    )}
                  >
                    {stats.totalPnl >= 0 ? "+" : "-"}$
                    {formatCurrency(Math.abs(stats.totalPnl), 2)}
                  </div>
                </div>
                <div className="text-xl text-muted-foreground">→</div>
                <div className="text-right">
                  <div className="text-[10px] text-muted-foreground">
                    Net P&L
                  </div>
                  <div
                    className={cn(
                      "text-sm font-bold",
                      stats.totalNetPnl >= 0
                        ? "text-bid-foreground"
                        : "text-ask-foreground"
                    )}
                  >
                    {stats.totalNetPnl >= 0 ? "+" : "-"}$
                    {formatCurrency(Math.abs(stats.totalNetPnl), 2)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnalysisCard>
      </div>
    </div>
  );
}

function formatDuration(ms: number): string {
  if (ms < 60 * 1000) return `${Math.round(ms / 1000)}s`;
  if (ms < 60 * 60 * 1000) return `${Math.round(ms / (60 * 1000))}m`;
  const hours = Math.floor(ms / (60 * 60 * 1000));
  const mins = Math.round((ms % (60 * 60 * 1000)) / (60 * 1000));
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

/**
 * Generate contextual insight for key metrics section
 */
function getKeyMetricsInsight(stats: {
  winRate: number;
  profitFactor: number;
  riskRewardRatio: number;
}): string {
  // Profit factor below 1 = losing money
  if (stats.profitFactor < 1) {
    return "Focus on cutting losses — your losing trades outweigh winners";
  }
  // Low win rate but good R:R can still be profitable
  if (stats.winRate < 40 && stats.riskRewardRatio >= 2) {
    return "Low win rate offset by strong risk/reward — quality over quantity";
  }
  // High win rate but poor R:R
  if (stats.winRate > 60 && stats.riskRewardRatio < 1) {
    return "High win rate but poor R:R — consider letting winners run";
  }
  // Solid fundamentals
  if (stats.profitFactor >= 1.5 && stats.winRate >= 45) {
    return "Solid fundamentals — maintain discipline and consistency";
  }
  // Default
  return "Core trading performance indicators";
}

/**
 * Generate contextual insight for risk metrics section
 */
function getRiskMetricsInsight(stats: {
  maxDrawdownPercent: number;
  recoveryFactor: number;
  sharpeRatio: number | null;
}): string {
  if (stats.maxDrawdownPercent > 30) {
    return "High drawdown — consider smaller position sizes";
  }
  if (stats.recoveryFactor < 1) {
    return "Not yet recovered from max drawdown";
  }
  if (stats.sharpeRatio && stats.sharpeRatio >= 2) {
    return "Excellent risk-adjusted returns";
  }
  if (stats.sharpeRatio && stats.sharpeRatio >= 1) {
    return "Good risk-adjusted performance";
  }
  return "Risk-adjusted performance";
}
